/**
 * PBX activity (ADR 0030): a read-only view of the Vodia change feed the
 * platform ingests — who changed which extension's field, when, from where.
 * Ops visibility beyond drift; filterable by domain and extension.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPbxActivity } from "@pbxmgr/api";
import { Alert, Button, Spinner, TextField } from "@pbxmgr/ui";

import { Panel } from "../dashboard/panels";

function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function ActivityPage(): ReactNode {
  const [domain, setDomain] = useState("");
  const [ext, setExt] = useState("");
  // Applied filters (only updated on submit, so typing doesn't spam the API).
  const [applied, setApplied] = useState<{ domain: string; ext: string }>({ domain: "", ext: "" });

  const activity = useQuery({
    queryKey: ["pbx-activity", applied],
    queryFn: () =>
      getPbxActivity({
        domain: applied.domain || undefined,
        ext: applied.ext || undefined,
        limit: 200,
      }),
  });
  const items = activity.data?.items ?? [];

  return (
    <div>
      <div className="dash__head">
        <h1>PBX activity</h1>
      </div>
      <Panel title="Recent changes on the PBX">
        <p>
          Config edits the platform ingested from each node’s change feed (ADR 0030), newest
          first. This is the same feed that drives backup-drift detection.
        </p>
        <form
          className="pairing-actions"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied({ domain: domain.trim(), ext: ext.trim() });
          }}
        >
          <TextField
            id="act-domain"
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
          <TextField
            id="act-ext"
            label="Extension"
            value={ext}
            onChange={(e) => setExt(e.target.value)}
          />
          <Button type="submit">Filter</Button>
        </form>

        {activity.isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <div className="ui-empty">
            No changes recorded yet. The feed is polled hourly; a new instance fills in over
            time.
          </div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Domain</th>
                <th>Ext</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={i}>
                  <td>{when(r.at)}</td>
                  <td>{r.who ?? "—"}</td>
                  <td>{r.domain}</td>
                  <td>{r.ext ?? "—"}</td>
                  <td>
                    {r.field
                      ? `${r.field}: "${r.old ?? ""}" → "${r.new ?? ""}"`
                      : r.msg}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {activity.isError ? <Alert>Failed to load activity.</Alert> : null}
      </Panel>
    </div>
  );
}
